// Structural JSON validation run before publishing the question + task templates.
// Pure functions, no imports — safe to unit-test in isolation.

export interface ValidationResult {
  ok: boolean;
  error?: string;
  /** Number of tasks, when validating a task template. */
  count?: number;
}

/**
 * Validate the task-template JSON before saving: it must parse, be an array, be non-empty,
 * and contain only objects (each task is an object).
 */
export function validateTaskTemplateJson(tasksJson: string | null | undefined): ValidationResult {
  if (tasksJson == null || tasksJson.trim() === '') {
    return { ok: false, error: 'task template is empty' };
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(tasksJson);
  } catch (e) {
    return { ok: false, error: `not valid JSON (${(e as Error)?.message ?? 'parse error'})` };
  }
  if (!Array.isArray(parsed)) {
    return { ok: false, error: 'expected a JSON array of tasks' };
  }
  if (parsed.length === 0) {
    return { ok: false, error: 'task list is empty' };
  }
  const badIdx = parsed.findIndex(t => t === null || typeof t !== 'object' || Array.isArray(t));
  if (badIdx >= 0) {
    return { ok: false, error: `task at index ${badIdx} is not an object` };
  }
  return { ok: true, count: parsed.length };
}

/**
 * Validate the SDM (question template) structure before publishing: it must be an object with
 * GlobalVariables, a non-empty TemplateVersion array whose first version has an ItemDef array,
 * and must serialize to JSON without error.
 */
export function validateSdmStructure(sdm: unknown): ValidationResult {
  if (!sdm || typeof sdm !== 'object') {
    return { ok: false, error: 'SDM is missing or not an object' };
  }
  const s = sdm as Record<string, any>;
  if (!s.GlobalVariables || typeof s.GlobalVariables !== 'object') {
    return { ok: false, error: 'GlobalVariables is missing' };
  }
  if (!Array.isArray(s.TemplateVersion) || s.TemplateVersion.length === 0) {
    return { ok: false, error: 'TemplateVersion is missing or empty' };
  }
  if (!Array.isArray(s.TemplateVersion[0]?.ItemDef)) {
    return { ok: false, error: 'TemplateVersion[0].ItemDef is missing' };
  }
  try {
    JSON.parse(JSON.stringify(sdm));
  } catch (e) {
    return { ok: false, error: `SDM is not serializable (${(e as Error)?.message ?? 'error'})` };
  }
  return { ok: true };
}
